import { useCallback, useContext, useMemo } from "react";
import { Outlet, useMatch } from "react-router-dom";
import clsx from "clsx";
import Navbar from "@components/Navbar";
import {
  VIDEO_ROOM,
  APPOINTMENT_DETAIL,
  APPOINTMENTS_BOOKING,
  APPOINTMENTS_FREE_TIME,
} from "@router/consts";
import { USER_ROLES } from "@utils/consts";
import { UserContext } from "@contexts/User";
import { AppointmentReportContext } from "@contexts/AppointmentReport";
import { AppointmentReportModal } from "./components";
import styles from "./RootLayout.module.scss";
import InternalReportModal from "@pages/Counsellor/Reports/InternalReportModal";
import NavigationGuide from "@components/NavigationGuide/NavigationGuide";
import { getNavigationGuideItems } from "@components/NavigationGuide/steps";

const RootLayout = ({ navItems }) => {
  const { user, profile } = useContext(UserContext);
  // Mobile bottom-tab layout is for the Student role only
  const isStudent = profile?.type === USER_ROLES.STUDENT_ROLE;
  const menuItems = useMemo(
    () => navItems?.filter((item) => item?.isMenu),
    [navItems],
  );
  const isVideoRoom = useMatch(VIDEO_ROOM);
  const isAppointmentDetails = useMatch(APPOINTMENT_DETAIL);
  const isAppointmentBooking = useMatch(APPOINTMENTS_BOOKING);
  const isAppointmentFreeTime = useMatch(APPOINTMENTS_FREE_TIME);
  const showNavbar =
    !isVideoRoom &&
    !(isAppointmentDetails && !isAppointmentBooking && !isAppointmentFreeTime);
  const guideItems = useMemo(
    () => getNavigationGuideItems(menuItems, user?.roles),
    [menuItems, user?.roles],
  );

  const { afterSurveyWrapStep, setAfterSurveyWrapStep } =
    useContext(UserContext);
  const { appointmentReportItem, internalReportItem } = useContext(
    AppointmentReportContext,
  );

  const stepsWrapHandler = useCallback(() => {
    const index =
      guideItems?.findIndex((item) => item.name === afterSurveyWrapStep) ?? -1;
    setAfterSurveyWrapStep(guideItems?.[index + 1]?.name ?? null);
  }, [afterSurveyWrapStep, guideItems, setAfterSurveyWrapStep]);
  const closeGuide = useCallback(
    () => setAfterSurveyWrapStep(null),
    [setAfterSurveyWrapStep],
  );

  return (
    <div
      className={clsx(
        "flex relative overflow-y-hidden",
        isStudent
          ? clsx("flex-col-reverse md:flex-row", styles.root)
          : "min-h-screen h-full max-h-screen flex-row",
      )}
    >
      {showNavbar && (
        <NavigationGuide
          items={guideItems}
          step={afterSurveyWrapStep}
          onNext={stepsWrapHandler}
          onClose={closeGuide}
        />
      )}
      {/* <AnimatePresence>
        {!!afterSurveyWrapStep && (
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
              transition: {
                type: "easyInOut",
              },
            }}
            exit={{
              opacity: 0,
            }}
            className="bg-black bg-opacity-80 absolute w-full h-full z-10 cursor-pointer"
            onClick={() => stepsWrapHandler()}
          />
        )}
      </AnimatePresence> */}
      {showNavbar &&
        !isVideoRoom &&
        !(
          isAppointmentDetails &&
          !isAppointmentBooking &&
          !isAppointmentFreeTime
        ) && <Navbar navItems={menuItems} />}

      <div
        className={clsx(
          "flex-1 flex justify-center bg-cararra min-h-0",
          !isVideoRoom && (isStudent ? "p-2 md:p-3" : "p-3"),
          styles.rootContainer,
          isStudent && styles.studentRootContainer,
        )}
      >
        {menuItems && <Outlet />}
      </div>
      <AppointmentReportModal
        isOpen={!!appointmentReportItem && !afterSurveyWrapStep}
      />
      <InternalReportModal
        isOpen={!!internalReportItem && !afterSurveyWrapStep}
      />
    </div>
  );
};

export default RootLayout;
